import {
  HASHTAG_REGEX,
  PROFILE_TAG_REGEX,
  SourceTypes
} from '../../../../constants';
import {
  buildProxyURL,
  formatText,
  getAuthor,
  getMediaWithProxy
} from '../../utils';
import type { DataValue, ProviderType, UserValue } from '../types';
import {
  tiktokHashtagURLRegExp,
  tiktokProfileURLRegExp
} from './tiktokURLPattern';
import type { TiktokResponseType, TiktokSourceValue } from './types';
import { getTiktokMedia } from './utils';

const PLATFORM = SourceTypes.TIKTOK;

export type TiktokPreparedSource = TiktokSourceValue & {
  author?: UserValue;
};

const select = (
  response: TiktokResponseType,
  widgetToken: string
): DataValue[] => {
  if (!response) {
    throw new Error('Empty response');
  }

  return response.map(
    ({
      id,
      carousel,
      publishedAt,
      counters,
      thumbnailURL,
      type,
      user,
      text: rawText
    }) => {
      const mediaType = type === 'image' ? 'photo' : 'video';
      const text = formatText(rawText, PLATFORM);
      const media = getTiktokMedia({
        id,
        type,
        thumbnailURL,
        carousel,
        widgetToken
      });

      return {
        id,
        text,
        media,
        timestamp: publishedAt * 1000,
        source: {
          type: PLATFORM,
          url: `https://www.tiktok.com/@${user.name}/${mediaType}/${id}`
        },
        counters: {
          comments: counters.comments,
          likes: counters.diggs
        },
        user: {
          name: user.name,
          verified: user.verified,
          image: getMediaWithProxy(user.image),
          url: `https://www.tiktok.com/@${user.name}`
        }
      };
    }
  );
};

export function createTiktokSourceProvider(
  sourceValue: TiktokPreparedSource,
  globalKey: string[],
  filtersKey: string[],
  widgetToken: string,
  updateSourceAuthor?: (sourceId: string, author: UserValue) => void
): ProviderType<TiktokResponseType> {
  const { source = '', connectionType } = sourceValue;
  let author: UserValue | undefined;

  const key = [...globalKey, ...filtersKey, source];

  const page = (data: TiktokResponseType | undefined) => {
    if (data) {
      return;
    }

    const requestParams = (() => {
      if (connectionType === 'tiktokProfile') {
        const [_all, profile = ''] =
          source.match(PROFILE_TAG_REGEX) ??
          source.match(tiktokProfileURLRegExp) ??
          [];

        return new URLSearchParams({
          type: 'profile',
          query: `${profile.toLocaleLowerCase()}`
        });
      }

      if (connectionType === 'tiktokHashtag') {
        const [_all, hashtag] =
          source.match(HASHTAG_REGEX) ??
          source.match(tiktokHashtagURLRegExp) ??
          [];

        return new URLSearchParams({
          type: 'hashtag',
          query: hashtag
        });
      }

      return undefined;
    })();

    return buildProxyURL({ apiSource: PLATFORM, params: requestParams });
  };

  const customSelect = (response: TiktokResponseType) => {
    const posts = select(response, widgetToken);

    if (connectionType === 'tiktokProfile' && author) {
      return posts.map((post) => ({
        ...post,
        user: {
          ...author,
          username: undefined
        }
      }));
    }

    return posts;
  };

  const customFetch = async (url: string) => {
    const response = await fetch(url);

    if (connectionType === 'tiktokProfile' && !author) {
      try {
        author = await getAuthor(sourceValue);

        if (author && updateSourceAuthor) {
          updateSourceAuthor(sourceValue.id, author);
        }
      } catch (error) {
        console.error('Failed to load TikTok author:', error);
      }
    }

    return response;
  };

  return { key, page, select: customSelect, fetch: customFetch };
}
